import type { PureQueryOptions } from '@apollo/client';
import type { BookmarkParentType } from '~/apollo/generated/v3/graphql';
import { BookmarkTargetType } from '~/apollo/generated/v3/graphql';
import { graphql } from '~/apollo/generated/v4';
import type {
  CrossSectionPartsFragment,
  FaciesPartsFragment,
  FilePartsFragment,
  GeoreferencePartsFragment,
  GigaPanPartsFragment,
  ProductionPartsFragment,
  ReservoirModelPartsFragment,
  SedimentaryLogPartsFragment,
  SoPicturePartsFragment,
  SyntheticWellLogPartsFragment,
  TrainingImagePartsFragment,
  VariogramPartsFragment,
  WellLogPartsFragment,
} from '~/apollo/generated/v4/graphql';
import { LifecycleStatus } from '~/apollo/generated/v4/graphql';
import type { TargetBookmarksManagerModalProps } from '~/components/bookmark/TargetBookmarksManagerModal';
import { Heading } from '~/components/common/Heading';
import { InfoIcon } from '~/components/common/icons/InfoIcon';
import { PictureThumbnailV4 } from '~/components/common/picture-thumbnail-v4';
import { PublishedContainer } from '~/components/common/published-container';
import { Tooltip } from '~/components/common/Tooltip';
import { Badge } from '~/components/ui/badge';
import { assertExhaustive } from '~/utils/common';
import { soBookmarkPath } from '~/utils/modules/bookmark';

export const soPictureParts = graphql(`
  fragment soPictureParts on Picture {
    ...pictureParts
    pictureUrl
    thumbnailUrl: pictureUrl(width: 300)
    file {
      ...fileParts
    }
  }
`);

type Picture = SoPicturePartsFragment;
type Pictures = { pictures: Picture[] };
type File = FilePartsFragment;
type Files = { files: File[] };
type Georeference = GeoreferencePartsFragment;
type Georeferences = { georeferences: Georeference[] };

type FaciesSchema = FaciesPartsFragment & Pictures;
export type CrossSection = CrossSectionPartsFragment &
  Pictures &
  Files &
  Georeferences;
export type SedimentaryLog = SedimentaryLogPartsFragment &
  Pictures &
  Files &
  Georeferences;
export type WellLog = WellLogPartsFragment & Pictures & Files & Georeferences;
export type SyntheticWellLog = SyntheticWellLogPartsFragment &
  Pictures &
  Files &
  Georeferences;
export type Production = ProductionPartsFragment & Pictures & Files;
export type ReservoirModel = ReservoirModelPartsFragment & Pictures & Files;
export type TrainingImage = TrainingImagePartsFragment & Pictures & Files;
export type Variogram = VariogramPartsFragment & Pictures & Files;
export type GigaPan = GigaPanPartsFragment & Pictures;

export type SupportingObject =
  | FaciesSchema
  | CrossSection
  | SedimentaryLog
  | WellLog
  | SyntheticWellLog
  | Production
  | ReservoirModel
  | TrainingImage
  | Variogram
  | GigaPan;

function soPictures(
  item: SupportingObject,
  withUnpublished: boolean,
): Picture[] {
  return item.pictures.filter(
    p => withUnpublished || p.lifecycleStatus === LifecycleStatus.Published,
  );
}

function soFiles(item: SupportingObject): File[] {
  switch (item.__typename) {
    case 'CrossSection':
    case 'SedimentaryLog':
    case 'WellLog':
    case 'SyntheticWellLog':
    case 'Production':
    case 'ReservoirModel':
    case 'TrainingImage':
    case 'Variogram':
      return item.files;
    case 'FaciesSchema':
    case 'GigaPan':
    case undefined:
      return [];
    default:
      throw assertExhaustive(item);
  }
}

type NumberFieldProps = {
  label: string;
  value?: number | null;
  helpText?: string;
};
function NumberField({ label, value, helpText }: NumberFieldProps) {
  if (typeof value !== 'number') return null;

  return (
    <tr>
      <td className="text-right p-1 cursor-pointer">
        <Tooltip disabled={!helpText} message={helpText ?? ''}>
          <span className="space-x-1">
            {label}
            {helpText && <InfoIcon />}
          </span>
        </Tooltip>
      </td>
      <td>
        <div className="p-1 bg-slate-100 font-mono">{value}</div>
      </td>
    </tr>
  );
}

export type BookmarkableSOProps = Pick<
  TargetBookmarksManagerModalProps,
  'parentType' | 'path'
>;

type Props = {
  item?: SupportingObject;
  fade?: boolean;
  numCols?: number;
  soParentType: 'outcrop' | 'study';
  soParentId: number;
  bookmarkParentType?: BookmarkParentType;
  bookmarkParentId?: number;
  showUnpublished?: boolean;
  refetchQueries: PureQueryOptions[];
};

export function SOItemDisplayV4({
  item,
  fade = false,
  soParentType,
  soParentId,
  bookmarkParentType,
  bookmarkParentId,
  showUnpublished = false,
  refetchQueries,
}: Props) {
  if (!item) return null;

  return (
    <PublishedContainer
      published={item.lifecycleStatus === LifecycleStatus.Published}
      withIcon
    >
      <div className="space-y-1">
        <Heading level={4}>{item.name}</Heading>

        <div className="text-sm text-slate-700 space-y-1">
          {'author' in item && item.author && (
            <div className="space-x-2">
              <Badge color="ghost">Author</Badge>
              <span>{item.author}</span>
            </div>
          )}

          {'interpretation' in item && item.interpretation && (
            <div className="space-x-2">
              <Tooltip message="Interpretation">
                <Badge color="ghost">Interp.</Badge>
              </Tooltip>
              <span>{item.interpretation}</span>
            </div>
          )}

          {'description' in item && (
            <div className="text-base">{item.description}</div>
          )}
        </div>

        {item.__typename === 'Variogram' && (
          <table>
            <tbody>
              <NumberField
                label="Nugget"
                value={item.nugget}
                helpText="Nugget n: The height of the jump of the semivariogram at the discontinuity at the origin."
              />
              <NumberField
                label="Sill"
                value={item.sill}
                helpText="Sill s: Limit of the variogram tending to infinity lag distances."
              />
              <NumberField
                label="Range"
                value={item.range}
                helpText="Range r: The distance in which the difference of the variogram from the sill becomes negligible. In models with a fixed sill, it is the distance at which this is first reached; for models with an asymptotic sill, it is conventionally taken to be the distance when the semivariance first reaches 95% of the sill."
              />
            </tbody>
          </table>
        )}

        <div className="space-y-2">
          {soPictures(item, showUnpublished).map(picture => (
            <PictureThumbnailV4
              key={picture.id}
              picture={picture}
              imgSrc={picture.pictureUrl}
              thumbnailImgSrc={picture.thumbnailUrl}
              fade={fade}
              bookmarkable={
                bookmarkParentType && bookmarkParentId
                  ? {
                      parentType: bookmarkParentType,
                      parentId: bookmarkParentId,
                      targetType: BookmarkTargetType.Picture,
                      targetId: parseInt(picture.id),
                      path: soBookmarkPath(
                        soParentType,
                        soParentId,
                        bookmarkParentType,
                        item.outcropTagId,
                      ),
                    }
                  : undefined
              }
              refetchQueries={refetchQueries}
            />
          ))}
        </div>

        {soFiles(item).map(file => (
          <div key={file.id}>
            <a
              href={file.signedUrl}
              target="_blank"
              rel="noreferrer"
              className="link break-words"
            >
              {file.name}
            </a>
          </div>
        ))}
      </div>
    </PublishedContainer>
  );
}
